import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import "./RubricEditor.css";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../config/firebaseConfig";
import { DEFAULT_RUBRICS } from "./DefaultRubrics";

const RubricEditor = ({
  show,
  onClose,
  onSave,
  initialRubric,
  maxScore,
  courseId,
}) => {
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [rubricData, setRubricData] = useState(
    Array(3)
      .fill()
      .map(() => Array(3).fill(""))
  );
  const [columnHeaders, setColumnHeaders] = useState(
    Array(3)
      .fill()
      .map((_, i) => `${i}/2`)
  );
  const [rowHeaders, setRowHeaders] = useState(Array(3).fill("Criteria"));
  const [rubricName, setRubricName] = useState("");
  const [selectedPreset, setSelectedPreset] = useState("");
  const [presets, setPresets] = useState([]);
  const [isLoadingPresets, setIsLoadingPresets] = useState(false);

  const generateColumnHeaders = (columns) => {
    return Array.from({ length: columns }, (_, i) => {
      return `${i}/${columns - 1}`;
    });
  };

  const initializeRubricData = (r, c) => {
    const initialCols = Math.max(2, c);
    const initialRows = Math.max(1, r);
    setRubricData(
      Array(initialRows)
        .fill()
        .map(() => Array(initialCols).fill(""))
    );
    setColumnHeaders(generateColumnHeaders(initialCols));
    setRowHeaders(Array(initialRows).fill("Criteria"));
    setCols(initialCols);
    setRows(initialRows);
  };

  useEffect(() => {
    const fetchPresets = async () => {
      setIsLoadingPresets(true);
      try {
        const fetchRubricsPreset = httpsCallable(
          functions,
          "fetchRubricsPreset"
        );
        const response = await fetchRubricsPreset({ courseId });
        const presetsArray = Object.values(response.data.presets);

        // Combine default rubrics with course-specific presets
        const defaultRubricsArray = Object.values(DEFAULT_RUBRICS);
        setPresets([...defaultRubricsArray, ...presetsArray]);
      } catch (error) {
        console.error("Error fetching rubric presets:", error);
        const defaultRubricsArray = Object.values(DEFAULT_RUBRICS);
        setPresets(defaultRubricsArray);
      } finally {
        setIsLoadingPresets(false);
      }
    };

    if (courseId) {
      fetchPresets();
    }
  }, [courseId]);

  useEffect(() => {
    if (initialRubric) {
      try {
        const data = Array.isArray(initialRubric.data)
          ? initialRubric.data
          : [];
        const cols = Array.isArray(initialRubric.columnHeaders)
          ? initialRubric.columnHeaders
          : generateColumnHeaders(3);
        const rows = Array.isArray(initialRubric.rowHeaders)
          ? initialRubric.rowHeaders
          : Array(3).fill("Criteria");

        setRows(rows.length);
        setCols(cols.length);
        setRubricData(
          data.length > 0
            ? data
            : Array(3)
                .fill()
                .map(() => Array(3).fill(""))
        );
        setColumnHeaders(cols);
        setRowHeaders(rows);
      } catch (error) {
        console.error("Error initializing rubric:", error);
        initializeRubricData(3, 3);
      }
    }
  }, [initialRubric, maxScore]);

  const handleHeaderChange = (index, value, isRow = true) => {
    if (isRow) {
      const newHeaders = [...rowHeaders];
      newHeaders[index] = value;
      setRowHeaders(newHeaders);
    } else {
      const newHeaders = [...columnHeaders];
      newHeaders[index] = value;
      setColumnHeaders(newHeaders);
    }
  };

  const handleCellChange = (rowIndex, colIndex, value) => {
    const newData = [...rubricData];
    newData[rowIndex][colIndex] = value;
    setRubricData(newData);
  };

  const addRow = () => {
    if (rows < 5) {
      setRows(rows + 1);
      setRubricData([...rubricData, Array(cols).fill("")]);
      setRowHeaders([...rowHeaders, "Criteria"]);
    }
  };

  const removeRow = () => {
    if (rows > 1) {
      setRows(rows - 1);
      setRubricData(rubricData.slice(0, -1));
      setRowHeaders(rowHeaders.slice(0, -1));
    }
  };

  const addColumn = () => {
    if (cols < 5) {
      const newCols = cols + 1;
      setCols(newCols);
      const newData = rubricData.map((row) => [...row, ""]);
      setRubricData(newData);
      setColumnHeaders(generateColumnHeaders(newCols));
    }
  };

  const removeColumn = () => {
    if (cols > 2) {
      const newCols = cols - 1;
      setCols(newCols);
      const newData = rubricData.map((row) => row.slice(0, -1));
      setRubricData(newData);
      setColumnHeaders(generateColumnHeaders(newCols));
    }
  };

  const handlePresetSelect = (presetId) => {
    // Check if it's a default rubric first
    if (presetId.startsWith("default-") || presetId.startsWith("essay-")) {
      const defaultRubric = DEFAULT_RUBRICS[presetId];
      if (defaultRubric) {
        setRubricData(defaultRubric.content);
        setRowHeaders(defaultRubric.rows);
        setColumnHeaders(defaultRubric.cols);
        setRows(defaultRubric.rows.length);
        setCols(defaultRubric.cols.length);
        setSelectedPreset(presetId);
        return;
      }
    }

    // Original code for custom presets
    const selectedRubric = presets.find((preset) => preset.id === presetId);
    if (selectedRubric) {
      try {
        // Handle custom presets that are stored as strings
        const content =
          typeof selectedRubric.content === "string"
            ? JSON.parse(
                selectedRubric.content
                  .replace(/^"|"$/g, "")
                  .replace(/\\"/g, '"')
              )
            : selectedRubric.content;

        const rows =
          typeof selectedRubric.rows === "string"
            ? JSON.parse(
                selectedRubric.rows.replace(/^"|"$/g, "").replace(/\\"/g, '"')
              )
            : selectedRubric.rows;

        const cols =
          typeof selectedRubric.cols === "string"
            ? JSON.parse(
                selectedRubric.cols.replace(/^"|"$/g, "").replace(/\\"/g, '"')
              )
            : selectedRubric.cols;

        if (
          !Array.isArray(content) ||
          !Array.isArray(rows) ||
          !Array.isArray(cols)
        ) {
          throw new Error("Invalid data structure");
        }

        setRubricData(content);
        setRowHeaders(rows);
        setColumnHeaders(cols);
        setRows(rows.length);
        setCols(cols.length);
        setSelectedPreset(presetId);
      } catch (error) {
        console.error("Error parsing rubric data:", error);
        initializeRubricData(3, 3);
        alert("Error loading rubric preset");
      }
    }
  };

  const handleSaveAsPreset = async () => {
    if (!rubricName.trim()) {
      alert("Please enter a name for the rubric preset");
      return;
    }

    if (!courseId || typeof courseId !== "string" || courseId.trim() === "") {
      console.error("Invalid courseId:", courseId);
      alert("Invalid course ID. Please try again.");
      return;
    }

    try {
      const saveRubricsPreset = httpsCallable(functions, "saveRubricsPreset");
      const response = await saveRubricsPreset({
        name: rubricName,
        rubricContent: rubricData,
        rowHeaders: rowHeaders,
        columnHeaders: columnHeaders,
        courseId,
      });

      const newPreset = {
        id: response.data.rubricId,
        name: rubricName,
        content: rubricData,
        rows: rowHeaders,
        cols: columnHeaders,
      };

      setPresets((prevPresets) => {
        const existingIndex = prevPresets.findIndex(
          (p) => p.name === rubricName
        );
        if (existingIndex !== -1) {
          // Update existing preset
          const updatedPresets = [...prevPresets];
          updatedPresets[existingIndex] = newPreset;
          return updatedPresets;
        }
        // Add new preset
        return [...prevPresets, newPreset];
      });

      setRubricName("");
      setSelectedPreset(response.data.rubricId);
    } catch (error) {
      console.error("Error saving rubric preset:", error);
      alert("Failed to save rubric preset. Please try again.");
    }
  };

  const handleDeletePreset = async (presetId) => {
    try {
      const deleteRubricsPreset = httpsCallable(
        functions,
        "deleteRubricsPreset"
      );
      await deleteRubricsPreset({ presetId, courseId });
      setPresets(presets.filter((preset) => preset.id !== presetId));
      if (selectedPreset === presetId) {
        setSelectedPreset("");
      }
    } catch (error) {
      console.error("Error deleting rubric preset:", error);
      alert("Failed to delete rubric preset");
    }
  };

  if (!show) return null;

  return (
    <div className="rubric-editor-overlay">
      <div className="rubric-editor-content">
        <div className="rubric-editor-header">
          <h2>Edit Rubric</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="rubric-editor-instructions">
          Managing rubrics is easy!
          <br />
          Simply select the rubric you would like to assign to this course from
          the dropdown menu below.
          <br />
          Note: If you would like to create a custom one, fill the cells below
          and click 'Save as Preset'.
        </div>

        <div className="grid-controls">
          <div className="preset-controls">
            <select
              value={selectedPreset || ""}
              onChange={(e) => {
                const presetId = e.target.value;
                setSelectedPreset(presetId);
                handlePresetSelect(presetId);
              }}
              className="preset-select"
            >
              <option value="">Select a preset...</option>
              {isLoadingPresets ? (
                <option disabled>Loading rubrics...</option>
              ) : (
                presets.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.name}
                  </option>
                ))
              )}
            </select>
            <div className="preset-save-controls">
              <input
                type="text"
                value={rubricName}
                onChange={(e) => setRubricName(e.target.value)}
                placeholder="Enter preset name"
              />
              <button onClick={handleSaveAsPreset}>Save as Preset</button>
              {selectedPreset && (
                <button
                  onClick={() => handleDeletePreset(selectedPreset)}
                  className="delete-preset"
                >
                  Delete Preset
                </button>
              )}
            </div>
          </div>

          <div className="row-col-controls">
            <div className="control-group">
              <label>Rows:</label>
              <button onClick={removeRow} disabled={rows <= 1}>
                <FontAwesomeIcon icon={faMinus} />
              </button>
              <span>{rows}</span>
              <button onClick={addRow} disabled={rows >= 5}>
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>

            <div className="control-group">
              <label>Columns:</label>
              <button onClick={removeColumn} disabled={cols <= 2}>
                <FontAwesomeIcon icon={faMinus} />
              </button>
              <span>{cols}</span>
              <button onClick={addColumn} disabled={cols >= 5}>
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>
          </div>
        </div>

        <div className="rubric-grid">
          <div className="rubric-row header-row">
            <div className="empty-corner-cell"></div>
            {columnHeaders.map((header, index) => (
              <input
                key={index}
                className="header-cell"
                value={header}
                readOnly
                placeholder={`${((index / cols) * 100).toFixed(0)}%`}
              />
            ))}
          </div>
          {rubricData.map((row, rowIndex) => (
            <div key={rowIndex} className="rubric-row">
              <input
                className="row-header-cell"
                value={rowHeaders[rowIndex]}
                onChange={(e) =>
                  handleHeaderChange(rowIndex, e.target.value, true)
                }
                placeholder="Criteria"
              />
              {row.map((cell, colIndex) => (
                <textarea
                  key={colIndex}
                  className="rubric-cell"
                  value={cell}
                  onChange={(e) =>
                    handleCellChange(rowIndex, colIndex, e.target.value)
                  }
                  placeholder="Enter description"
                />
              ))}
            </div>
          ))}
        </div>

        <div className="editor-buttons">
          <button
            onClick={async () => {
              try {
                const setActiveRubric = httpsCallable(
                  functions,
                  "setActiveRubric"
                );
                await setActiveRubric({
                  courseId,
                  rubricData,
                  rowHeaders,
                  columnHeaders,
                  presetId: selectedPreset || null,
                });

                onSave({
                  rubricId: selectedPreset || null,
                  data: rubricData,
                  rowHeaders,
                  columnHeaders,
                });
              } catch (error) {
                console.error("Error setting active rubric:", error);
                alert("Failed to set active rubric. Please try again.");
              }
            }}
            className="save-button"
          >
            Save Rubric
          </button>
          <button onClick={onClose} className="cancel-button">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default RubricEditor;
